// Version history: every save of this resume, with a one-click restore.
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, RotateCcw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type Version = { id: string; created_at: string; label: string | null; template: string | null; file_url: string | null; resume_json: any };

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function HistoryPanel({ rowId, refreshKey, onRestore }: { rowId?: string; refreshKey: unknown; onRestore: (json: any) => Promise<void> }) {
  const [list, setList] = useState<Version[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!rowId) return;
    let live = true;
    (async () => {
      const { data, error } = await (supabase as any).from('resume_versions').select('*').eq('tailored_document_id', rowId).order('created_at', { ascending: false }).limit(30);
      if (!live) return;
      if (error) { console.error('Version history failed:', error); setFailed(true); return; }
      setFailed(false);
      setList(data as Version[]);
    })();
    return () => { live = false; };
  }, [rowId, refreshKey]);

  if (!rowId) return <p className="text-sm text-muted-foreground">Open this resume from its results page to use version history.</p>;
  if (failed) return <p className="text-sm text-red-700">Sorry, we couldn't load your versions right now. Please try again in a few minutes.</p>;
  if (!list) return <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</p>;
  if (!list.length) return <p className="text-sm text-muted-foreground">No saved versions yet. Each change you save appears here.</p>;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-foreground">Saved versions</h4>
      {list.map((v, i) => (
        <div key={v.id} className="flex items-center justify-between gap-2 rounded-md border p-2 text-sm">
          <div className="min-w-0">
            <div className="font-medium">{when(v.created_at)}{i === 0 ? ' (latest)' : ''}</div>
            <div className="truncate text-xs text-muted-foreground">{v.label || 'Saved'}</div>
          </div>
          <Button size="sm" variant="outline" className="gap-1.5" disabled={!!busy} onClick={async () => { setBusy(v.id); try { await onRestore(v.resume_json); } finally { setBusy(''); } }}>
            {busy === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Restore
          </Button>
        </div>
      ))}
    </div>
  );
}
